import isInsuranceSupported from 'checkout/selectors/request/isInsuranceSupported'
import getCheckoutInsuranceQuotes from 'checkout/selectors/view/getCheckoutInsuranceQuotes'
import getInsuranceItemsView from 'checkout/selectors/view/itemViews/getInsuranceItemsView'
import { max, sum } from 'lib/array/arrayUtils'
import { createSelector } from 'reselect'
import { getIsEnabledLuxPlusDiscountedInsurance } from 'luxPlus/selectors/featureToggle'
import getIsCheckoutInsuranceFetching from 'checkout/selectors/view/getIsCheckoutInsuranceFetching'

export const getAvailableInsuranceBenefits = createSelector(
  (state: App.State) => getIsEnabledLuxPlusDiscountedInsurance(state),
  (state: App.State) => isInsuranceSupported(state),
  (state: App.State) => getCheckoutInsuranceQuotes(state),
  (state: App.State) => getInsuranceItemsView(state),
  (state: App.State) => getIsCheckoutInsuranceFetching(state),
  (
    isEnabledLuxPlusDiscountedInsurance,
    insuranceSupported,
    quotes,
    insuranceItemsView,
    isFetchingInsurance,
  ): App.Checkout.LuxPlusInsuranceBenefits => {
    const maxSavings = max(quotes.map(quote => quote.luxPlusSavings)) ?? 0

    // Flight CFMR's memberPrice (per getInsuranceItemsView) is already 0 for anyone not
    // genuinely eligible, gated server-side by svc-insurance independently of
    // isEnabledLuxPlusDiscountedInsurance (which only ever gated the legacy, service-wide-disabled
    // generic insurance discount). So these totals must NOT be zeroed by that flag, or a real
    // flight-embedded-protection discount silently disappears from the checkout savings summary
    // whenever the legacy flag happens to be off.
    // memberPrice of 0 means "no member pricing for this item" (per getInsuranceItemsView), not
    // "member pays nothing" - so totalMemberPriceSavings must use the same >0 guard the other
    // benefit selectors use (e.g. accommodation.ts), or an ineligible item's full price gets
    // counted as "saved". totalMemberPrice itself stays a raw sum: it's the signal other code
    // reads to know whether real member pricing exists at all (nonzero = eligible).
    const totalPrice = sum(insuranceItemsView.data, item => item.totals.price)
    const totalMemberPrice = sum(insuranceItemsView.data, item => item.totals.memberPrice)
    const totalMemberPriceSavings = sum(insuranceItemsView.data, item => item.totals.memberPrice > 0 ? item.totals.price - item.totals.memberPrice : 0)

    if (!isEnabledLuxPlusDiscountedInsurance) {
      return {
        isAvailable: false,
        fetching: false,
        luxPlusDiscountPercentage: 0,
        luxPlusDiscountValueDisplayThreshold: 0,
        totalMemberMaxSavings: 0,
        totalMemberPriceSavings,
        totalPrice,
        totalMemberPrice,
        totalBaseMemberPrice: totalMemberPrice,
      }
    }

    return {
      isAvailable: isEnabledLuxPlusDiscountedInsurance && insuranceSupported,
      fetching: insuranceSupported && isFetchingInsurance,
      // Todo: remove the default values (this affects the upsell tile in the order confirmation page)
      luxPlusDiscountPercentage: quotes[0]?.luxPlusDiscountPercentage || 40,
      // Todo: remove the default values (this affects the upsell tile in the order confirmation page)
      luxPlusDiscountValueDisplayThreshold: quotes[0]?.luxPlusDiscountValueDisplayThreshold || 30,
      totalMemberMaxSavings: maxSavings,
      totalMemberPriceSavings,
      totalPrice,
      totalMemberPrice,
      // TODO - revisit this when benefit is available to customers
      totalBaseMemberPrice: totalMemberPrice,
    }
  },
)
